import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import { LuMail, LuLock, LuMapPin, LuEye, LuEyeOff, LuShieldAlert } from 'react-icons/lu';
import Modal from '../../components/common/Modal';

export default function Login() {
  const [form, setForm] = useState({ email: '', password: '', role: 'admin' });
  const [rememberMe, setRememberMe] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const { login, loading } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await login(form.email, form.password, form.role, rememberMe);
    if (result.success) { toast.success('Welcome back!'); navigate('/dashboard'); }
    else toast.error(result.message);
  };

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  return (
    <div className="min-h-screen flex">
      {/* Left — brand panel */}
      <div className="hidden lg:flex lg:w-[45%] flex-col items-center justify-center relative overflow-hidden bg-gradient-to-br from-hajj-dark via-[#1a3a36] to-[#0f2420] p-10">
        {/* Background dots / pattern */}
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(#fff 1px, transparent 1px)', backgroundSize: '28px 28px' }} />
        {/* Gold glow */}
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-hajj-gold/10 rounded-full blur-3xl" />

        <div className="relative z-10 text-center max-w-xs">
          <div className="mx-auto mb-6 w-20 h-20 rounded-3xl bg-gradient-to-br from-hajj-gold to-[#b8922a] flex items-center justify-center text-4xl text-white shadow-gold">
            <LuMapPin />
          </div>
          <h1 className="text-4xl font-extrabold text-white mb-2 tracking-wide">الموجه الآلي</h1>
          <p className="text-hajj-gold text-xs uppercase tracking-[3px] mb-6">Smart Transport for Pilgrims</p>
          <p className="text-white/60 text-sm leading-relaxed mb-8">
            Hajj Pilgrimage Management System — coordinating drivers, agents, and trips for a seamless pilgrim journey.
          </p>
          <div className="flex flex-col gap-3 text-left">
            {['Company & Agent Management', 'Real-time Trip Scheduling', 'Driver & Route Coordination'].map(f => (
              <div key={f} className="flex items-center gap-3 text-white/75 text-sm">
                <span className="text-hajj-gold text-xs">✦</span>{f}
              </div>
            ))}
          </div>
        </div>

        {/* Bottom gradient */}
        <div className="absolute bottom-0 left-0 right-0 h-32 from-hajj-gold/8 to-transparent pointer-events-none" />
      </div>

      {/* Right — login form */}
      <div className="flex-1 flex items-center justify-center bg-hajj-cream px-6 py-10">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-hajj-gold to-[#b8922a] flex items-center justify-center text-white text-xl">
              <LuMapPin />
            </div>
            <div>
              <div className="font-bold text-hajj-dark">الموجه الآلي</div>
              <div className="text-[11px] text-gray-400 uppercase tracking-widest">Hajj Management</div>
            </div>
          </div>

          <h2 className="text-3xl font-extrabold text-hajj-dark mb-1">Welcome back!</h2>
          <p className="text-gray-400 text-sm mb-7">Sign in to your management portal</p>

          {/* Role toggle */}
          <div className="flex bg-white border border-gray-100 rounded-xl p-1 gap-1 mb-6 shadow-hajj-sm">
            {[{ val: 'admin', label: 'Super Admin' }, { val: 'company', label: 'Company' }].map(({ val, label }) => (
              <button
                key={val}
                type="button"
                onClick={() => setForm(f => ({ ...f, role: val }))}
                className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  form.role === val
                    ? 'bg-hajj-dark text-white shadow-hajj-sm'
                    : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-5">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Email Address</label>
              <div className="relative">
                <LuMail className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-lg pointer-events-none" />
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={form.email}
                  onChange={set('email')}
                  required
                  className="input-field pl-10"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Password</label>
              <div className="relative">
                <LuLock className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-lg pointer-events-none" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={form.password}
                  onChange={set('password')}
                  required
                  className="input-field pl-10 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword ? <LuEyeOff size={16} /> : <LuEye size={16} />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={e => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-hajj-dark accent-hajj-dark cursor-pointer"
                />
                <span className="text-sm text-gray-500">Remember me</span>
              </label>
              <button
                type="button"
                onClick={() => setForgotOpen(true)}
                className="text-sm text-hajj-teal hover:underline"
              >
                Forgot password?
              </button>
            </div>

            <button type="submit" disabled={loading} className="btn-primary justify-center py-3 mt-1">
              {loading
                ? <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                : 'Sign In'}
            </button>
          </form>
        </div>
      </div>
      <Modal isOpen={forgotOpen} onClose={() => setForgotOpen(false)} title="Forgot Password" size="sm">
        <div className="flex flex-col items-center gap-4 py-2 text-center">
          <div className="w-14 h-14 rounded-2xl bg-hajj-gold/10 flex items-center justify-center text-hajj-gold">
            <LuShieldAlert size={28} />
          </div>
          <div>
            <p className="text-sm font-semibold text-hajj-dark mb-1">Password Reset Required</p>
            <p className="text-sm text-gray-500 leading-relaxed">
              Please contact your system administrator to reset your password.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setForgotOpen(false)}
            className="btn-primary justify-center w-full mt-1"
          >
            OK, Got it
          </button>
        </div>
      </Modal>
    </div>
  );
}
